import { find as loFind, omit, values } from 'lodash';
import cloneDeep from 'lodash/cloneDeep';
import each from 'lodash/each';
import isEmpty from 'lodash/isEmpty';
import {
  ConditionGroupInput,
  Filter,
  FilterOption,
  SearchLandingPage,
  SearchPageInfo,
  SortInput
} from '../../types/search';

export const getSearchPages = (): SearchPageInfo[] => {
  const searchPageString = process.env.GATSBY_SEARCH_PAGES;
  if (searchPageString) {
    return JSON.parse(searchPageString);
  } else {
    return [];
  }
};

export const getSearchPageInfo = (pageId: string): SearchPageInfo | null => {
  const pageInfo = getSearchPages().filter((pageInfo: SearchPageInfo) => pageInfo.pageId === pageId);
  if (pageInfo[0]) {
    return pageInfo[0];
  } else {
    console.error('search page config not found', pageId);
    return null;
  }
};

export const getSearchPageIdFromType = (type: string): string => {
  const pageInfo = getSearchPages().filter((pageInfo: SearchPageInfo) => pageInfo.types?.includes(type));
  if (pageInfo[0]) {
    return pageInfo[0].pageId as string;
  } else {
    console.error('search page info not found for type', type);
    return '';
  }
};

export const hasSearchPageInfoByPageId = (pageId: string): boolean => {
  const searchPageString = process.env.GATSBY_SEARCH_PAGES;
  let searchPageData;
  if (searchPageString) {
    searchPageData = JSON.parse(searchPageString);
  }
  if (searchPageData && loFind(searchPageData, { pageId: pageId })) {
    return true;
  }
  return false;
};

export const getSearchPageInfoByPageId = (pageId: string): SearchPageInfo | null => {
  const searchPageString = process.env.GATSBY_SEARCH_PAGES;
  let searchPageData;
  if (searchPageString) {
    searchPageData = JSON.parse(searchPageString);
  }
  if (searchPageData) {
    return loFind(searchPageData, { pageId: pageId });
  }
  return null;
};

export const createConditionGroup = (filters: {
  [key: string]: Array<string> | string;
}): ConditionGroupInput | null => {
  const conditionGroups: any = [];
  let i = 0;
  each(filters, (filter: any, index: any) => {
    if (index == 'sort') {
      return;
    }
    conditionGroups[i] = {
      conjunction: 'OR',
      conditions: [],
      tags: [`facet:${index}`]
    };
    if (typeof filter === 'object' && !Array.isArray(filter) && filter !== null) {
      if (!filter.values || !filter.operator) {
        return;
      }
      conditionGroups[i].conditions.push({
        name: index,
        value: filter.values,
        operator: filter.operator
      });
    } else {
      each(filter, (value) => {
        conditionGroups[i].conditions.push({
          name: index,
          value: value,
          operator: '='
        });
      });
    }
    i++;
  });
  let conditionGroup: ConditionGroupInput | null = null;
  if (!isEmpty(conditionGroups)) {
    conditionGroup = {
      conjunction: 'AND',
      groups: conditionGroups
    };
  }
  return conditionGroup;
};

export const rebuildPathifyOptions = (options: Array<any>, optionReferences: Array<any>, destination: any = {}) => {
  const getVal = (object: any, path: any) => {
    const regex = /(?:\/)(?!")[^\]]+|(?:\/)[^"]+|[^."[\]]+/g;
    return path.match(regex)?.reduce((res: any, prop: any) => res[prop], object);
  };

  const cleanUp = (destination: any) => {
    const newDestination = values(destination);
    newDestination.forEach((destination) => {
      if (destination.children) {
        destination.children = cleanUp(destination.children);
      }
    });
    return newDestination;
  };

  options.forEach((option, _index) => {
    let currentPath = '';
    let _destination = destination;

    const split = option.path.split('.');
    split.forEach((value: any, index: any) => {
      currentPath = `${currentPath}${currentPath.length > 0 ? '.' + value : value}`;
      const matchedOption = getVal(optionReferences, currentPath);
      !_destination[matchedOption.id] && (_destination[matchedOption.id] = omit(matchedOption, ['children', 'path']));
      if (split.length > 1 && split.length != index + 1) {
        if (!_destination[matchedOption.id].children) {
          _destination[matchedOption.id].children = {};
        }
        _destination = _destination[matchedOption.id].children;
      }
    });
  });

  return cleanUp(destination);
};

export const mapAndPathifyOptions = (
  options: Array<any>,
  optionReferences: Array<any>,
  destination: Array<any> = [],
  parentPath: string = ''
) => {
  options.forEach((option, index) => {
    const reference = loFind(optionReferences, (optionReference: any) => {
      return optionReference.filter.replace(/["]+/g, '') === option.label;
    });
    option.path = `${parentPath}[${index}]`;
    if (reference) {
      const name = getFilterOptionName(reference.filter);
      option.id = name;
      option.label = name;
      option.count = reference.count;
      option.meta = null;
      destination.push(option);
      option.children &&
        mapAndPathifyOptions(option.children, optionReferences, destination, `${option.path}.children`);
    }
  });
  return destination;
};

export const getFilterOptionName = (label: string) => {
  return label.replace(/["]+/g, '');
};

export const getFilterOptions = (facets: any, filter: Filter, profileDetails: any) => {
  const facetName = filter.id;
  let flatFilters: Array<FilterOption> = [];
  if (facets) {
    const facet = facets.filter((facetItem: any) => {
      return facetItem.name == facetName ? true : false;
    });
    if (
      filter?.fieldGroup &&
      profileDetails?.fieldGroups &&
      profileDetails?.fieldGroups[filter?.fieldGroup].fields[filter.id]
    ) {
      if (!isEmpty(facet)) {
        const optionsList = cloneDeep(profileDetails.fieldGroups[filter.fieldGroup].fields[filter.id].options);
        flatFilters = mapAndPathifyOptions(optionsList, facet[0].values);
        return rebuildPathifyOptions(flatFilters, optionsList);
      }
      return flatFilters;
    } else {
      if (!isEmpty(facet)) {
        each(facet[0].values, (value) => {
          const name = getFilterOptionName(value.filter);
          flatFilters.push({
            id: name,
            label: name,
            count: value.count,
            meta: null
          });
        });
      }
    }
  }
  return flatFilters;
};

/**
 * Get the id of the sort filter, return a default / intial value if one isnt available.
 * @param filter Filter & { initial?: string; default?: string }
 * @param value string
 * @param keys string
 * @returns string
 */
export const getSearchSortFilterId = (
  filter: Filter & { initial?: string; default?: string },
  value: string,
  keys: string
): string => (value ? value : keys.length > 0 ? (filter.initial as string) : (filter.default as string));

/**
 * Get the sort filter values from the id passed in.
 * @param filter Filter & { initial?: string; default?: string }
 * @param id string
 * @returns { SortInput[] }
 */
export const getSearchSortFilterValue = (
  filter: Filter & { initial?: string; default?: string },
  id: string
): SortInput[] | null => {
  const sort: any = filter.options.filter((sortItem: any) => sortItem.id === id)[0];
  return sort
    ? [
        {
          field: sort.meta?.id,
          value: sort.meta?.direction
        }
      ]
    : null;
};

/**
 * Return structured object for search values, determined by the search params on the URL
 * @param params URLSearchParams
 * @param filters Filter[]
 * @returns { [key: string]: Array<string> | string }
 */
export const getSearchValuesFromURL = (
  params: URLSearchParams,
  filters: Filter[]
): { [key: string]: Array<string> | string } => {
  const values: { [key: string]: Array<string> | string } = {};
  params?.forEach((value: string, key: string) => {
    if (key === 'search') {
      value.length > 0 && (values[key] = value);
    } else if (value && value.length > 0) {
      const filter = filters.filter((value: Filter) => value.id === key)[0];
      const multiFilter = filter && filter.multi ? true : false;
      !values[key] && multiFilter && (values[key] = []);
      if (multiFilter) (values[key] as Array<string>).push(value);
      else values[key] = value;
    }
  });
  return values;
};

/**
 * Create URLSearchParams from object
 * @param data { [key: string]: string | Array<string> }
 * @returns URLSearchParams
 */
export const createURLSearchParams = (data: { [key: string]: string | Array<string> }): URLSearchParams => {
  const params = new URLSearchParams();
  for (const param in data) {
    if (data[param] && data[param]?.length > 0) {
      switch (Array.isArray(data[param])) {
        case true:
          (data[param] as Array<string>).forEach((value: string) => value.length > 0 && params.append(param, value));
          break;
        case false:
          data[param].length > 0 && !param.match(/(hash|utm_(.*))/) && params.append(param, data[param] as string);
          break;
      }
    }
  }
  return params;
};

/**
 * Transform raw data into SearchLandingPage
 * @param data {any}
 * @returns
 */
export const transformSearchLandingPage = (data: any): SearchLandingPage => {
  return {
    id: data.drupal_id || data.id,
    created: data.created,
    title: data.title,
    path: data.path?.alias,
    sectors: data.relationships?.field_sectors
      ? (data.relationships?.field_sectors ?? [])?.map((sector: any) => sector.label)
      : [],
    opportunityType: !isEmpty(data.relationships?.field_opportunity_type)
      ? data.relationships?.field_opportunity_type[0]?.label
      : null,
    city: !isEmpty(data.relationships?.field_city) ? data.relationships?.field_city[0]?.label : ''
  };
};
